/**
 * Pure, UI-side derivations over worker-supplied RouteAnalyticsView.
 *
 * Everything here is presentation arithmetic (ratios, shares, chart series).
 * Authoritative simulation math (demand, spill, pricing response) stays in the
 * Web Worker; the numbers below only re-express what the worker reported.
 */

import { SLOT_BUSINESS_SPLIT_THRESHOLD, departureSlot } from '../../../domain/economics/yieldEngine';
import type {
  CabinClass,
  PassengerDemandView,
  RouteAnalyticsView,
  RouteDailySnapshot,
} from '../../../domain/models/contracts';

// ---------------------------------------------------------------------------
// KPIs
// ---------------------------------------------------------------------------

export interface RouteKpis {
  readonly revenueUSD: number;
  readonly operatingCostUSD: number;
  readonly netProfitUSD: number;
  readonly loadFactorPct: number;
  /** Revenue per Available Seat Mile, USD. */
  readonly rasmUSD: number;
  /** Cost per Available Seat Mile, USD. */
  readonly casmUSD: number;
  readonly operatingMarginPct: number;
}

export function computeKpis(route: RouteAnalyticsView): RouteKpis {
  const netProfitUSD = route.revenueUSD - route.operatingCostUSD;
  const asm = route.availableSeatMiles;
  return {
    revenueUSD: route.revenueUSD,
    operatingCostUSD: route.operatingCostUSD,
    netProfitUSD,
    loadFactorPct: route.loadFactorPct,
    rasmUSD: asm > 0 ? route.revenueUSD / asm : 0,
    casmUSD: asm > 0 ? route.operatingCostUSD / asm : 0,
    operatingMarginPct:
      route.revenueUSD > 0 ? (netProfitUSD / route.revenueUSD) * 100 : 0,
  };
}

// ---------------------------------------------------------------------------
// Price sensitivity
// ---------------------------------------------------------------------------

export type PriceResistance = 'LOW' | 'BALANCED' | 'HIGH';

export interface PriceSensitivity {
  readonly cabin: CabinClass;
  /** Proposed fare divided by the market baseline fare. */
  readonly fareRatioToMarket: number;
  /** Estimated demand change vs. demand at the market baseline fare, in %. */
  readonly demandChangeVsMarketPct: number;
  /** Estimated demand change vs. demand at the currently published fare, in %. */
  readonly demandChangeVsCurrentPct: number;
  readonly rating: PriceResistance;
}

const MIN_RATIO = 0.05;
const MAX_RATIO = 20;

function clampRatio(numerator: number, denominator: number): number {
  if (!Number.isFinite(numerator) || !Number.isFinite(denominator) || denominator <= 0) {
    return 1;
  }
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, numerator / denominator));
}

/**
 * Constant-elasticity estimate: demand ∝ fare^ε with ε < 0 (worker-supplied).
 * Rating is driven by the estimated demand lost versus the market baseline:
 *   > -5%  -> LOW price resistance
 *   > -20% -> BALANCED
 *   else   -> HIGH
 */
export function assessPriceSensitivity(
  cabin: CabinClass,
  proposedFare: number,
  route: RouteAnalyticsView,
): PriceSensitivity {
  const elasticity = -Math.abs(route.priceElasticityByCabin[cabin]);
  const ratioToMarket = clampRatio(proposedFare, route.marketBaselineFares[cabin]);
  const ratioToCurrent = clampRatio(proposedFare, route.currentFares[cabin]);

  const demandChangeVsMarketPct = (Math.pow(ratioToMarket, elasticity) - 1) * 100;
  const demandChangeVsCurrentPct = (Math.pow(ratioToCurrent, elasticity) - 1) * 100;

  let rating: PriceResistance;
  if (demandChangeVsMarketPct > -5) {
    rating = 'LOW';
  } else if (demandChangeVsMarketPct > -20) {
    rating = 'BALANCED';
  } else {
    rating = 'HIGH';
  }

  return {
    cabin,
    fareRatioToMarket: ratioToMarket,
    demandChangeVsMarketPct,
    demandChangeVsCurrentPct,
    rating,
  };
}

export const PRICE_RESISTANCE_LABEL: Readonly<Record<PriceResistance, string>> = {
  LOW: 'Low price resistance',
  BALANCED: 'Balanced',
  HIGH: 'High price resistance',
};

// ---------------------------------------------------------------------------
// Demand waterfall
// ---------------------------------------------------------------------------

export type WaterfallBarId = 'potential' | 'unmet' | 'carried';

export interface WaterfallBar {
  readonly id: WaterfallBarId;
  readonly label: string;
  /** Invisible offset so the bar floats; 0 for the two anchor bars. */
  readonly base: number;
  readonly value: number;
}

/** Potential -> Unmet (floating drop) -> Carried. */
export function buildWaterfall(demand: PassengerDemandView): readonly WaterfallBar[] {
  const potential = Math.max(0, demand.totalPotential);
  const unmet = Math.min(potential, Math.max(0, demand.unmet));
  const carried = Math.max(0, demand.carried);
  return [
    { id: 'potential', label: 'Potential demand', base: 0, value: potential },
    { id: 'unmet', label: 'Unmet demand', base: potential - unmet, value: unmet },
    { id: 'carried', label: 'Carried', base: 0, value: carried },
  ];
}

// ---------------------------------------------------------------------------
// Spill & leakage
// ---------------------------------------------------------------------------

export type LeakageKey =
  | 'capacitySpill'
  | 'priceLeakage'
  | 'scheduleLeakage'
  | 'comfortLeakage';

export interface LeakageSlice {
  readonly key: LeakageKey;
  readonly label: string;
  readonly description: string;
  readonly passengers: number;
  /** Share of all categorised lost passengers, 0-100. */
  readonly sharePct: number;
  /** Share of total potential demand, 0-100. */
  readonly ofPotentialPct: number;
  /** Suggested player lever for this leak. */
  readonly lever: string;
}

const LEAKAGE_META: ReadonlyArray<{
  readonly key: LeakageKey;
  readonly label: string;
  readonly description: string;
  readonly lever: string;
}> = [
  {
    key: 'capacitySpill',
    label: 'Capacity spill',
    description: 'Flights were 100% full, so extra passengers were turned away.',
    lever: 'Add frequencies, up-gauge the aircraft, or raise fares.',
  },
  {
    key: 'priceLeakage',
    label: 'Price leakage',
    description: 'Lost to cheaper competitors or to the no-fly option.',
    lever: 'Lower fares toward the market baseline or cut costs.',
  },
  {
    key: 'scheduleLeakage',
    label: 'Schedule leakage',
    description: 'Poor departure hours or long connection layovers.',
    lever: 'Retime departures or tighten hub connection banks.',
  },
  {
    key: 'comfortLeakage',
    label: 'Comfort leakage',
    description: 'Lost to rivals with better seat pitch or amenities.',
    lever: 'Increase pitch, add Wi-Fi / in-seat power, or add premium cabins.',
  },
];

export function buildLeakageSlices(demand: PassengerDemandView): readonly LeakageSlice[] {
  const values: Readonly<Record<LeakageKey, number>> = {
    capacitySpill: Math.max(0, demand.capacitySpill),
    priceLeakage: Math.max(0, demand.priceLeakage),
    scheduleLeakage: Math.max(0, demand.scheduleLeakage),
    comfortLeakage: Math.max(0, demand.comfortLeakage),
  };
  const total =
    values.capacitySpill +
    values.priceLeakage +
    values.scheduleLeakage +
    values.comfortLeakage;
  const potential = Math.max(0, demand.totalPotential);

  return LEAKAGE_META.map((meta) => {
    const passengers = values[meta.key];
    return {
      key: meta.key,
      label: meta.label,
      description: meta.description,
      lever: meta.lever,
      passengers,
      sharePct: total > 0 ? (passengers / total) * 100 : 0,
      ofPotentialPct: potential > 0 ? (passengers / potential) * 100 : 0,
    };
  });
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------

export const TREND_WINDOW_DAYS = 30;

export function trailingHistory(
  history: readonly RouteDailySnapshot[],
  days: number = TREND_WINDOW_DAYS,
): readonly RouteDailySnapshot[] {
  return [...history].sort((a, b) => a.day - b.day).slice(-days);
}

// ---------------------------------------------------------------------------
// Cost breakdown and diagnostics
// ---------------------------------------------------------------------------

export type CostKey = 'fuel' | 'airport' | 'crewMaintenance' | 'capital';

export interface CostSlice {
  readonly key: CostKey;
  readonly label: string;
  readonly detail: string;
  readonly usd: number;
  /** Share of the four categories, 0-100. */
  readonly sharePct: number;
}

/** Fuel, airport fees, crew and maintenance, and capital charge, as shares of their total. */
export function buildCostBreakdown(route: RouteAnalyticsView): readonly CostSlice[] {
  const parts: ReadonlyArray<{ key: CostKey; label: string; detail: string; usd: number }> = [
    {
      key: 'fuel',
      label: 'LTO & cruise fuel',
      detail: `$${Math.round(route.ltoFuelCostUSD).toLocaleString('en-US')} of it is landing and take-off burn, paid on every departure`,
      usd: route.fuelCostUSD,
    },
    {
      key: 'airport',
      label: 'Airport & MTOW fees',
      detail: 'Landing fees on maximum take-off weight, passenger charges, noise surcharges',
      usd: route.airportFeesUSD,
    },
    {
      key: 'crewMaintenance',
      label: 'Crew & maintenance reserve',
      detail: 'Flight and cabin crew, airframe and engine reserve per flight hour and cycle',
      usd: route.crewMaintenanceUSD,
    },
    {
      key: 'capital',
      label: 'Capital charge / lease',
      detail: "This route's share of the aircraft's lease or ownership cost",
      usd: route.capitalChargeUSD,
    },
  ];
  const total = parts.reduce((sum, part) => sum + Math.max(0, part.usd), 0);
  return parts.map((part) => ({
    ...part,
    usd: Math.max(0, part.usd),
    sharePct: total > 0 ? (Math.max(0, part.usd) / total) * 100 : 0,
  }));
}

export type DiagnosticId = 'FIXED_DEPARTURE_COSTS' | 'LOW_LOAD_FACTOR' | 'OFF_PEAK_DEPARTURE';

export interface RouteDiagnostic {
  readonly id: DiagnosticId;
  readonly message: string;
}

/** Operating margin below which a route is diagnosed. */
export const DIAGNOSTIC_MARGIN_PCT = 5;
export const FIXED_COST_SHARE_LIMIT_PCT = 60;
export const LOW_LOAD_FACTOR_PCT = 65;

/**
 * Advice for a route that loses money or earns under a 5% operating margin. Each rule reads the cost split the
 * worker reported; a profitable route gets none.
 */
export function diagnoseRoute(route: RouteAnalyticsView): readonly RouteDiagnostic[] {
  const kpis = computeKpis(route);
  if (route.operatedFlights === 0 || (kpis.netProfitUSD > 0 && kpis.operatingMarginPct >= DIAGNOSTIC_MARGIN_PCT)) {
    return [];
  }
  const diagnostics: RouteDiagnostic[] = [];

  const slices = buildCostBreakdown(route);
  const total = slices.reduce((sum, slice) => sum + slice.usd, 0);
  // Every departure pays these whatever the stage length: airport and MTOW fees, and the LTO share of fuel.
  const departureFixed = route.airportFeesUSD + route.ltoFuelCostUSD;
  if (total > 0 && (departureFixed / total) * 100 > FIXED_COST_SHARE_LIMIT_PCT) {
    diagnostics.push({
      id: 'FIXED_DEPARTURE_COSTS',
      message:
        'High takeoff and landing fees relative to stage length. Consider down-gauging to a Turboprop or Regional Jet.',
    });
  }
  if (route.loadFactorPct < LOW_LOAD_FACTOR_PCT) {
    diagnostics.push({
      id: 'LOW_LOAD_FACTOR',
      message:
        'Seat capacity exceeds local O&D demand. Reduce daily flight frequency or route through a hub to capture connecting passenger feed.',
    });
  }
  const slot = departureSlot(route.departureLocalHour);
  const peak = slot === 'MORNING_PEAK' || slot === 'EVENING_PEAK';
  if (!peak && route.businessShare > SLOT_BUSINESS_SPLIT_THRESHOLD) {
    diagnostics.push({
      id: 'OFF_PEAK_DEPARTURE',
      message:
        'Flight departs during off-peak hours with low business yield. Re-time departure to peak business slots (07:00-09:00 or 17:00-19:00).',
    });
  }
  return diagnostics;
}
